'use client';

import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import { Box, HStack, Portal, Separator, Text, VStack } from '@chakra-ui/react';
import { Button } from '@m/components-ui/button';
import { APP_STORE_URL } from '@m/constants/Contacts';
import { MetropolisGlyph } from '@m/icons';

import { ABOUT_HREF, FOR_REAL_ESTATE_HREF, SIGN_IN_URL } from '@/constants/links';

/** Matches PageHeader height (theme space 20). */
const HEADER_OFFSET = '80px';

interface MobileMenuProps {
  onClose: () => void;
}

export function MobileMenu({ onClose }: MobileMenuProps) {
  const firstFocusRef = useRef<HTMLAnchorElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body.style.overflow;
    firstFocusRef.current?.focus();
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [onClose]);

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusable = containerRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <Portal>
      {/*
        Must sit above sticky SiteHeader banner (1200). Previous zIndex={1000}
        left close controls under the header so the menu could not be dismissed.
        Panel starts below the elevated header; backdrop click still closes.
      */}
      <Box
        position="fixed"
        top={HEADER_OFFSET}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.600"
        zIndex="overlay"
        onClick={onClose}
        aria-hidden
      />
      <Box
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        position="fixed"
        top={HEADER_OFFSET}
        left={0}
        bottom={0}
        w="calc(100% - 56px)"
        bg="fill.background.default"
        zIndex="modal"
        display="flex"
        flexDirection="column"
        overflowY="auto"
        onKeyDown={handleKeyDown}
        boxShadow="lg"
        pt={6}
        px={4}
        pb={8}
      >
        <VStack align="stretch" gap={0}>
          <Button asChild size="lg" variant="tertiary" w="full" mb={6}>
            <a ref={firstFocusRef} href={SIGN_IN_URL} rel="noopener noreferrer" target="_blank">
              Log in or Sign up
            </a>
          </Button>

          <Box asChild py={4}>
            <a href={ABOUT_HREF} target="_blank" rel="noopener noreferrer">
              <Text color="text.primary" variant="MainBodyMedium">
                About
              </Text>
            </a>
          </Box>
          <Box asChild py={4}>
            <a href={FOR_REAL_ESTATE_HREF} target="_blank" rel="noopener noreferrer">
              <Text color="text.primary" variant="MainBodyMedium">
                For Real Estate Owners
              </Text>
            </a>
          </Box>

          <Separator my={6} />

          <HStack align="center" gap={3} mb={4}>
            <Box fontSize="40px" lineHeight={1} flexShrink={0}>
              <MetropolisGlyph />
            </Box>
            <Text color="text.primary" variant="MainBodyRegular">
              The experience is better in the app.
            </Text>
          </HStack>
          <Button asChild size="lg" variant="primary" w="full">
            <a href={APP_STORE_URL} rel="noopener noreferrer" target="_blank">
              Download now
            </a>
          </Button>
        </VStack>
      </Box>
    </Portal>
  );
}
